<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

interface PreviewDiagnosis {
  reference: string
  description: string
}

interface PreviewDiagnosisGroup {
  currentDP: PreviewDiagnosis | null
  currentDR: PreviewDiagnosis | null
  associatedDiags: PreviewDiagnosis[]
}

const props = defineProps<{
  diagnosisGroup: PreviewDiagnosisGroup
}>()

const { t } = useI18n()

const diagnosisGroup = computed(() => props.diagnosisGroup)
</script>

<template>
  <div class="card-container">
    <div class="principal-diagnosis-group mb-3">
      <div class="card diagnosis-card">
        <div class="card-body">
          <h1 class="card-title d-flex align-items-center">
            <span>DP</span>
            <small class="ms-1 fs-6">- {{ t('stayDisplayer.mco.diagnosisStatus.proposal') }}</small>
          </h1>
        </div>
        <div class="card-body diagnosis-title">
          <template v-if="diagnosisGroup.currentDP">
            <h2 :title="diagnosisGroup.currentDP.description">
              {{ diagnosisGroup.currentDP.reference }}
            </h2>
            <p>{{ diagnosisGroup.currentDP.description }}</p>
          </template>
          <span v-else>{{ t('stayDisplayer.mco.noDiagnosis', { diagnosisType: 'DP' }) }}</span>
        </div>
      </div>
    </div>

    <div
      v-if="diagnosisGroup.currentDR"
      class="related-diagnosis-group mb-3"
    >
      <div class="card diagnosis-card">
        <div class="card-body">
          <h1 class="card-title d-flex align-items-center">
            <span>DR</span>
            <small class="ms-1 fs-6">- {{ t('stayDisplayer.mco.diagnosisStatus.proposal') }}</small>
          </h1>
        </div>
        <div class="card-body diagnosis-title">
          <h2 :title="diagnosisGroup.currentDR.description">
            {{ diagnosisGroup.currentDR.reference }}
          </h2>
          <p>{{ diagnosisGroup.currentDR.description }}</p>
        </div>
      </div>
    </div>

    <div
      v-if="diagnosisGroup.associatedDiags.length"
      class="associated-diagnosis-group"
    >
      <div class="card diagnosis-card">
        <div class="card-body">
          <h1 class="associated-title card-title d-flex align-items-center">
            <span>DAS</span>
            <small class="ms-1 fs-6">- {{ t('stayDisplayer.mco.diagnosisStatus.proposal') }}</small>
          </h1>
        </div>
        <div class="card-body">
          <div class="row g-0">
            <div
              v-for="das in diagnosisGroup.associatedDiags"
              :key="das.reference"
              class="col-12 mb-1"
            >
              <div class="card-body diagnosis-title p-1">
                <h2 :title="das.description">
                  {{ das.reference }}
                </h2>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card-title {
  font-size: 2rem;
}

h2 {
  font-size: 1.1rem;
}

.associated-title {
  font-size: 2rem;
}
</style>
